<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import type { HTMLAttributeClass } from '@fondof-ui/types'
import Modal from '@satch-ui/components/modal/Modal.vue'
import Status from '@satch-ui/components/status/Status.vue'
import { t as uiTranslation } from '@satch-ui/translations/translations'
import {
  triggerCloseStyleSwitcherEvent,
  triggerOpenStyleSwitcherEvent,
  triggerSelectStyleSwitcherEvent,
} from '@shared/event/events/style-switcher'
import type { ProductWithVariants } from '@shared/shop-api/product/types'
import ProductCard from '@storefront-satch/components/molecules/product/card/ProductCard.vue'
import ProductImage from '@storefront-satch/components/molecules/product/card/ProductImage.vue'
import StyleSwitcherButton from '@storefront-satch/components/molecules/style-switcher/StyleSwitcherButton.vue'
import { t } from '@storefront-satch/lib/translations'

type Props = {
  style: string
  colorCode: string
  products: ProductWithVariants[]
} & HTMLAttributeClass

const props = defineProps<Props>()
const isOpen = ref<boolean>(false)

const activeProduct = computed(() => {
  return props.products.find(
    (product) => product.attributes.style === props.style
  )
})

watch(isOpen, () => {
  if (isOpen.value && activeProduct.value) {
    triggerOpenStyleSwitcherEvent({
      currentProduct: activeProduct.value,
    })
  }

  if (!isOpen.value && activeProduct.value) {
    triggerCloseStyleSwitcherEvent({
      currentProduct: activeProduct.value,
    })
  }
})

const trackOnSelect = (product: ProductWithVariants) => {
  if (!activeProduct.value) {
    return
  }

  triggerSelectStyleSwitcherEvent({
    currentProduct: activeProduct.value,
    selectedProduct: product,
  })
}
</script>

<template>
  <StyleSwitcherButton
    :class="props.class"
    :color-badge="props.colorCode"
    :text="props.style"
    @click="isOpen = !isOpen"
  />

  <Modal
    :is-open="isOpen"
    @close="isOpen = false"
  >
    <template #header>
      <div class="text-md">
        {{ t('productView.styleSwitcher.title') }}
      </div>
    </template>

    <template #content>
      <div class="xs:mb-0 h-full w-full bg-gray-200 sm:mb-0 sm:px-9 md:mb-0">
        <div
          :class="[
            'pb-7.5 gap-x-4 gap-y-5 overflow-y-auto px-5 py-5 md:py-9 md:pl-2 md:pr-5',
            'grid max-h-[58vh] w-full grid-cols-[repeat(3,1fr)] md:max-h-[476px] md:grid-cols-[repeat(6,1fr)]',
          ]"
        >
          <template
            v-for="(product, index) in products"
            :key="product.id"
          >
            <div
              :data-el-id="`grid-item-${index}`"
              :class="[
                'relative hover:outline hover:outline-2 hover:outline-gray-400',
                {
                  'outline-primary outline outline-2':
                    product.id === activeProduct?.id,
                },
              ]"
            >
              <ProductCard
                :product="product"
                :hide-edition="true"
                @click="trackOnSelect(product)"
              >
                <template #image>
                  <ProductImage :product="product" />
                </template>
              </ProductCard>

              <Status
                v-if="!product.available"
                class="absolute bottom-0"
                variant="soldOut"
                padding="small"
              >
                {{ uiTranslation('product.soldOut') }}
              </Status>
            </div>
          </template>
        </div>
      </div>
    </template>
  </Modal>
</template>
